import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showModal } from 'src/actions/ModalActions';
import { addToast } from 'src/actions/ToastActions';
import translations from 'src/lang';

import { buildStanleyAudienceShareData } from '../../api/buildStanleyAudienceShareData';
import { createTemporaryAudienceForModal } from '../../api/createTemporaryAudienceForModal';
import { toShareAudienceSelectedItem } from '../../api/toShareAudienceSelectedItem';
import { StanleyMembershipTargetContext } from '../../api/hooks/use-stanley-query-membership-size/buildStanleyMembershipBody';
import { StanleyQuerySuggestion } from '../../api/types';

export const useShareStanleyQuerySuggestion = (
  suggestion: StanleyQuerySuggestion
) => {
  const dispatch = useDispatch();
  const [isPreparingShare, setIsPreparingShare] = useState(false);
  const dataSource = useSelector(
    (state: { queryBuilder: { dataSource: string } }) =>
      state.queryBuilder.dataSource
  );
  const target = useSelector((state) => {
    const targetSelected = state.queryBuilder.target_selected;
    if (!targetSelected || !state.queryBuilder?.targets) {
      return null;
    }
    return state.queryBuilder.targets[targetSelected] as
      | StanleyMembershipTargetContext
      | undefined;
  });

  const sharePayload = useMemo(
    () => buildStanleyAudienceShareData(dataSource, suggestion, target),
    [dataSource, suggestion, target]
  );

  const handleShare = useCallback(async () => {
    if (!sharePayload || isPreparingShare) {
      return;
    }

    setIsPreparingShare(true);
    try {
      const audience = sharePayload.id
        ? { ...sharePayload, id: sharePayload.id as string | number }
        : await createTemporaryAudienceForModal(sharePayload);

      dispatch(
        showModal(
          {
            isOpen: true,
            position: 'center',
            selectedItem: toShareAudienceSelectedItem(audience),
          },
          'ShareAudience'
        )
      );
    } catch {
      dispatch(
        addToast({
          status: 'error',
          message: translations['en-US'].TOASTS.CREATE_AUDIENCE_ERROR,
        })
      );
    } finally {
      setIsPreparingShare(false);
    }
  }, [dispatch, isPreparingShare, sharePayload]);

  return {
    handleShare,
    canShare: Boolean(sharePayload),
    isPreparingShare,
  };
};
